import React, { useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { STOCK_IMAGES } from '../../config/constants';
import {
  X,
  Check,
  Upload,
  Image as ImageIcon,
  Link,
  Trash2,
  Sparkles,
  Building,
  Anchor,
  Laptop,
  CheckCircle2,
  FolderOpen,
} from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentImageUrl?: string;
  onSelectImage: (imageUrl: string, altText?: string) => void;
  title?: string;
  aspectRatioHint?: string;
}

export const ImagePickerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  currentImageUrl,
  onSelectImage,
  title = 'Görsel Seç / Değiştir',
  aspectRatioHint = 'Önerilen oran: 16:9 veya 4:3 (Maks 10MB)',
}) => {
  const { activeSite, updateSiteData } = useSite();

  const [activeTab, setActiveTab] = useState<'stock' | 'upload' | 'url'>('stock');
  const [selectedUrl, setSelectedUrl] = useState<string>(currentImageUrl || '');
  const [customUrl, setCustomUrl] = useState<string>(currentImageUrl || '');
  const [altText, setAltText] = useState<string>('');
  const [stockCategory, setStockCategory] = useState<'all' | 'corporate' | 'modern' | 'tech' | 'yacht'>('all');
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; url: string }>>([
    {
      name: 'modern_office_exterior.jpg',
      url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop',
    },
    {
      name: 'corporate_teamwork.jpg',
      url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1600&auto=format&fit=crop',
    },
    {
      name: 'financial_growth.jpg',
      url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1600&auto=format&fit=crop',
    },
    {
      name: 'yacht_luxury_cruise.jpg',
      url: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=1600&auto=format&fit=crop',
    },
    {
      name: 'technology_cloud_data.jpg',
      url: 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=1600&auto=format&fit=crop',
    },
    {
      name: 'consulting_meeting.jpg',
      url: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=1600&auto=format&fit=crop',
    },
  ]);

  if (!isOpen) return null;

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        const newFile = { name: file.name, url: base64 };
        setUploadedFiles((prev) => [newFile, ...prev]);
        setSelectedUrl(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleConfirm = () => {
    const finalUrl = activeTab === 'url' ? customUrl : selectedUrl;
    if (finalUrl) {
      onSelectImage(finalUrl, altText);
    }
    onClose();
  };

  const handleRemove = () => {
    onSelectImage('');
    onClose();
  };

  const filteredStock = STOCK_IMAGES.filter((img) => {
    if (stockCategory === 'all') return true;
    if (stockCategory === 'corporate') return img.name.includes('Ofis') || img.name.includes('Plaza') || img.name.includes('Yönetim');
    if (stockCategory === 'yacht') return img.name.includes('Yat') || img.name.includes('Gulet') || img.name.includes('Marina');
    if (stockCategory === 'tech') return img.name.includes('Teknoloji') || img.name.includes('Kodlama');
    return true;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden text-slate-100 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2 text-blue-400">
            <ImageIcon className="w-5 h-5" />
            <div>
              <h3 className="font-extrabold text-base text-white tracking-tight">{title}</h3>
              <p className="text-[11px] text-slate-400">{aspectRatioHint}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="px-6 pt-3 flex border-b border-slate-800 gap-6 text-xs font-bold bg-slate-950/40">
          <button
            type="button"
            onClick={() => setActiveTab('stock')}
            className={`pb-3 border-b-2 flex items-center gap-1.5 transition ${
              activeTab === 'stock'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Hazır Stok Kütüphanesi</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('upload')}
            className={`pb-3 border-b-2 flex items-center gap-1.5 transition ${
              activeTab === 'upload'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Upload className="w-3.5 h-3.5" />
            <span>Yüklenenler / Dosya Seç</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('url')}
            className={`pb-3 border-b-2 flex items-center gap-1.5 transition ${
              activeTab === 'url'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Link className="w-3.5 h-3.5" />
            <span>Web URL Girişi</span>
          </button>
        </div>

        {/* Body Content */}
        <div className="p-6 space-y-4 overflow-y-auto custom-scrollbar flex-1">
          {/* TAB 1: STOCK PHOTOS */}
          {activeTab === 'stock' && (
            <div className="space-y-4">
              {/* Category Pills */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs">
                {[
                  { id: 'all', label: 'Tümü' },
                  { id: 'corporate', label: 'Kurumsal & Ofis' },
                  { id: 'tech', label: 'Teknoloji & SaaS' },
                  { id: 'yacht', label: 'Yat & Denizcilik' },
                ].map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setStockCategory(cat.id as any)}
                    className={`px-3 py-1.5 rounded-full font-bold transition whitespace-nowrap ${
                      stockCategory === cat.id
                        ? 'bg-blue-600 text-white shadow'
                        : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Grid Images */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredStock.map((img, index) => {
                  const isSelected = selectedUrl === img.url;
                  return (
                    <div
                      key={img.url || index}
                      onClick={() => setSelectedUrl(img.url)}
                      className={`group relative rounded-2xl overflow-hidden border-2 cursor-pointer transition aspect-video bg-slate-800 ${
                        isSelected
                          ? 'border-blue-500 ring-2 ring-blue-500/50 scale-[1.02]'
                          : 'border-slate-800 hover:border-slate-600'
                      }`}
                    >
                      <img
                        src={img.url}
                        alt={img.name}
                        className="w-full h-full object-cover transition duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition p-2 flex flex-col justify-end">
                        <span className="text-[10px] font-bold text-white truncate">{img.name}</span>
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg">
                          <Check className="w-3.5 h-3.5" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 2: UPLOADS & DRAG DROP */}
          {activeTab === 'upload' && (
            <div className="space-y-4">
              <div className="p-8 border-2 border-dashed border-slate-700 hover:border-blue-500 rounded-3xl bg-slate-950/40 text-center space-y-3 transition">
                <div className="w-12 h-12 rounded-full bg-blue-500/10 text-blue-400 mx-auto flex items-center justify-center">
                  <Upload className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-white">Görselinizi Buraya Bırakın</h4>
                  <p className="text-xs text-slate-400 mt-0.5">JPG, PNG, WebP veya SVG (Maks. 10MB)</p>
                </div>
                <div>
                  <label className="inline-block px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold shadow-md cursor-pointer transition">
                    Bilgisayardan Dosya Seç
                    <input type="file" onChange={handleFileUpload} className="hidden" accept="image/*" />
                  </label>
                </div>
              </div>

              {uploadedFiles.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Son Yüklenenler</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {uploadedFiles.map((file, idx) => {
                      const isSelected = selectedUrl === file.url;
                      return (
                        <div
                          key={idx}
                          onClick={() => setSelectedUrl(file.url)}
                          className={`group relative rounded-2xl overflow-hidden border-2 cursor-pointer transition aspect-video bg-slate-800 ${
                            isSelected
                              ? 'border-blue-500 ring-2 ring-blue-500/50 scale-[1.02]'
                              : 'border-slate-800 hover:border-slate-600'
                          }`}
                        >
                          <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                          {isSelected && (
                            <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg">
                              <Check className="w-3.5 h-3.5" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: CUSTOM URL */}
          {activeTab === 'url' && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-300">Görsel Web URL'si</label>
                <input
                  type="url"
                  value={customUrl}
                  onChange={(e) => {
                    setCustomUrl(e.target.value);
                    setSelectedUrl(e.target.value);
                  }}
                  placeholder="https://images.unsplash.com/photo-..."
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {customUrl && (
                <div className="rounded-2xl overflow-hidden border border-slate-700 max-h-52 aspect-video bg-slate-950 flex items-center justify-center">
                  <img
                    src={customUrl}
                    alt="URL Önizleme"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Alt Text Setting */}
          <div className="space-y-1.5 pt-2 border-t border-slate-800">
            <label className="text-xs font-bold text-slate-400">SEO & Erişilebilirlik Alt Metni (İsteğe Bağlı)</label>
            <input
              type="text"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Örn: Cihan Holding Modern Maslak Ofis Binası"
              className="w-full px-3.5 py-2 rounded-xl bg-slate-800 border border-slate-700 text-xs text-white focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 flex items-center justify-between bg-slate-900/60">
          <button
            type="button"
            onClick={handleRemove}
            className="px-3.5 py-2 rounded-xl text-xs font-bold text-rose-400 hover:bg-rose-500/10 transition flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>Görseli Kaldır</span>
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition"
            >
              İptal
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-5 py-2.5 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white shadow-lg flex items-center gap-1.5 transition active:scale-95"
            >
              <Check className="w-4 h-4" />
              <span>Görseli Uygula</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
